/**
 * LatestReleaseCard — Admin-curated "Latest Release" card shown under the
 * hero LiveStats block. Two flavors:
 *   - release_type === "free": green "DOWNLOAD NOW" CTA → external GTA5-Mods link
 *   - release_type === "paid": white "PURCHASE NOW" CTA → Tebex Headless checkout
 *     (same flow as the products cart: creates basket, handles FiveM auth,
 *      redirects to Tebex hosted checkout).
 *
 * Design intentionally matches the rest of the hero: thin border, dark surface,
 * uppercase mono kickers, generous spacing — NOT a generic product card.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Download, ShoppingCart, ArrowUpRight, Loader2 } from "lucide-react";
import { useCfxAuth } from "@/context/CfxAuthContext";
import { tebexCheckoutLatestRelease } from "@/api";

export default function LatestReleaseCard({ release }) {
  const { user, login } = useCfxAuth();
  const [submitting, setSubmitting] = useState(false);

  if (!release || !release.enabled) return null;
  const { title, description, image, platform, release_type, link, tebex_package_id, button_label } = release;

  const isFree = release_type !== "paid";
  // Free needs a link; paid needs a tebex_package_id. If neither is set, hide the card.
  if (!title) return null;
  if (isFree && !link) return null;
  if (!isFree && !tebex_package_id) return null;

  const defaultLabel = isFree ? "DOWNLOAD NOW" : "PURCHASE NOW";
  const label = (button_label || defaultLabel).toUpperCase();
  const Icon = isFree ? Download : ShoppingCart;

  const handlePaidClick = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!user) {
      toast.error("Please log in with Cfx.re to continue");
      login();
      return;
    }
    setSubmitting(true);
    try {
      const data = await tebexCheckoutLatestRelease();
      const next = data.auth_url || data.checkout_url;
      if (next) {
        toast.success(data.auth_url ? "Linking your FiveM account on Tebex..." : "Redirecting to Tebex checkout...");
        window.location.href = next;
      } else {
        toast.error("Tebex did not return a redirect URL");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Checkout failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Shared visual chrome — wrapper acts as <a> for free, <button> for paid.
  // Uses `nm-latest-release-glow` (see App.css) for a subtle animated halo
  // that draws attention without being noisy. Hover lifts and intensifies.
  const sharedOuterClass =
    "nm-latest-release-glow group relative block bg-[var(--theme-surface)]/80 backdrop-blur-sm border border-[var(--theme-border)] hover:border-[var(--theme-accent-primary)] hover:-translate-y-0.5 rounded-2xl transition-all duration-300 overflow-hidden w-full text-left";

  const body = (
    <>
      {/* Header strip */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[var(--theme-border)]">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--theme-text-muted)]">
          <span
            className={`w-1.5 h-1.5 rounded-full animate-pulse ${
              isFree ? "bg-emerald-400" : "bg-white"
            }`}
          />
          // LATEST_RELEASE
        </div>
        <div className="flex items-center gap-2">
          {/* FEATURED badge — subtle accent, always visible so the card reads as premium */}
          <span
            className="nm-featured-badge font-mono text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 border border-[var(--theme-accent-primary)]/60 text-[var(--theme-accent-primary)] bg-[var(--theme-accent-primary)]/5"
            data-testid="latest-release-badge"
          >
            ★ FEATURED
          </span>
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--theme-text-muted)]">
            {(platform || (isFree ? "GTA5-MODS" : "TEBEX")).toUpperCase()}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex gap-4 p-5">
        {image ? (
          <div className="relative w-24 h-24 flex-shrink-0 border border-[var(--theme-border)] bg-black overflow-hidden rounded-xl">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            {/* corner accent */}
            <span className="absolute top-0 left-0 w-3 h-px bg-[var(--theme-accent-primary)]" />
            <span className="absolute top-0 left-0 w-px h-3 bg-[var(--theme-accent-primary)]" />
            <span className="absolute bottom-0 right-0 w-3 h-px bg-[var(--theme-accent-primary)]" />
            <span className="absolute bottom-0 right-0 w-px h-3 bg-[var(--theme-accent-primary)]" />
          </div>
        ) : null}

        <div className="flex-1 min-w-0">
          <h3
            className="font-display text-lg md:text-xl font-bold tracking-tight text-[var(--theme-text)] leading-tight line-clamp-2"
            title={title}
          >
            {title}
          </h3>
          {description ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--theme-text-muted)] mt-2 leading-relaxed line-clamp-2">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {/* CTA strip */}
      <div className="px-5 pb-5">
        <div
          className={`relative flex items-center justify-between gap-3 px-4 py-3 border transition ${
            isFree
              ? "border-emerald-400/40 bg-emerald-500/5 group-hover:bg-emerald-500/15 group-hover:border-emerald-400"
              : "border-[var(--theme-text)]/40 bg-white/5 group-hover:bg-white/15 group-hover:border-[var(--theme-text)]"
          } ${submitting ? "opacity-60" : ""}`}
        >
          <div className="flex items-center gap-2">
            {submitting ? (
              <Loader2 size={14} className="animate-spin text-[var(--theme-text)]" />
            ) : (
              <Icon
                size={14}
                className={isFree ? "text-emerald-400" : "text-[var(--theme-text)]"}
              />
            )}
            <span
              className={`font-mono text-[11px] uppercase tracking-[0.28em] font-bold ${
                isFree ? "text-emerald-300" : "text-[var(--theme-text)]"
              }`}
            >
              {submitting ? "PROCESSING..." : label}
            </span>
          </div>
          <ArrowUpRight
            size={14}
            className={`transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
              isFree ? "text-emerald-400" : "text-[var(--theme-text)]"
            }`}
          />
          {isFree && (
            <span className="pointer-events-none absolute inset-0 ring-1 ring-emerald-400/20 group-hover:ring-emerald-400/60 transition" />
          )}
        </div>
      </div>
    </>
  );

  if (isFree) {
    return (
      <a
        data-testid="latest-release-card"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={sharedOuterClass}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      data-testid="latest-release-card"
      type="button"
      onClick={handlePaidClick}
      disabled={submitting}
      className={`${sharedOuterClass} disabled:cursor-not-allowed`}
    >
      {body}
    </button>
  );
}
